// redux/api/longDrive/LongDriveApi.js
import { createApi } from "@reduxjs/toolkit/query/react";
import CustomFetchBase from "../CustomFetchBase";

export const LongDriveApi = createApi({
  reducerPath: "LongDriveApi",
  baseQuery: CustomFetchBase,
  tagTypes: ["LONGDRIVEAPI"],
  endpoints: (build) => ({
    // User Endpoints
    getAllLongDriveBookings: build.query({
      query: ({
        page = 1,
        status = "",
        vehicleType = "",
        fromDate = "",
        toDate = "",
        search = "",
      }) => ({
        url: `/longdrive/all-bookings?page=${page}&status=${status}&vehicleType=${vehicleType}&fromDate=${fromDate}&toDate=${toDate}&search=${search}`,
        method: "GET",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      providesTags: ["LONGDRIVEAPI"],
    }),

    getUserLongDriveBookings: build.query({
      query: ({ page = 1, status = "" }) => ({
        url: `/longdrive/my-bookings?page=${page}&status=${status}`,
        method: "GET",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      providesTags: ["LONGDRIVEAPI"],
    }),

    getLongDriveBookingById: build.query({
      query: ({ id }) => ({
        url: `/longdrive/${id}`,
        method: "GET",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      providesTags: ["LONGDRIVEAPI"],
    }),

    createLongDriveBooking: build.mutation({
      query: (data) => ({
        url: `/longdrive`,
        method: "POST",
        body: data,
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: ["LONGDRIVEAPI"],
    }),

    cancelLongDriveBooking: build.mutation({
      query: ({ id, reason }) => ({
        url: `/longdrive/admin/cancel/${id}`,
        method: "PUT",
        body: { reason },
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: ["LONGDRIVEAPI"],
    }),

    estimateLongDriveFare: build.mutation({
      query: (data) => ({
        url: `/longdrive/estimate-fare`,
        method: "POST",
        body: data,
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
    }),

    // Driver Endpoints
    getDriverLongDriveBookings: build.query({
      query: ({ page = 1, status = "" }) => ({
        url: `/longdrive/driver/bookings?page=${page}&status=${status}`,
        method: "GET",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      providesTags: ["LONGDRIVEAPI"],
    }),

    driverAcceptBooking: build.mutation({
      query: ({ id }) => ({
        url: `/longdrive/driver/accept/${id}`,
        method: "PUT",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: ["LONGDRIVEAPI"],
    }),

    driverStartJourney: build.mutation({
      query: ({ id }) => ({
        url: `/longdrive/driver/start/${id}`,
        method: "PUT",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: ["LONGDRIVEAPI"],
    }),

    driverCompleteJourney: build.mutation({
      query: ({ id, actualDuration }) => ({
        url: `/longdrive/driver/complete/${id}`,
        method: "PUT",
        body: { actualDuration },
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: ["LONGDRIVEAPI"],
    }),

    // Admin Endpoints
    setPricing: build.mutation({
      query: ({ id, pricingData }) => ({
        url: `/longdrive/admin/set-pricing/${id}`,
        method: "PUT",
        body: pricingData,
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: ["LONGDRIVEAPI"],
    }),

    assignDriver: build.mutation({
      query: ({ id, driverId }) => ({
        url: `/longdrive/admin/assign-driver/${id}`,
        method: "PUT",
        body: { driverId },
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: ["LONGDRIVEAPI"],
    }),

    updateBookingStatus: build.mutation({
      query: ({ id, status, notes }) => ({
        url: `/longdrive/admin/update-status/${id}`,
        method: "PUT",
        body: { status, notes },
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: ["LONGDRIVEAPI"],
    }),

    getPendingReviews: build.query({
      query: () => ({
        url: `/longdrive/admin/pending-reviews`,
        method: "GET",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      providesTags: ["LONGDRIVEAPI"],
    }),

    getAvailableDrivers: build.query({
      query: ({ vehicleType = "" }) => ({
        url: `/longdrive/admin/available-drivers?vehicleType=${vehicleType}`,
        method: "GET",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      providesTags: ["LONGDRIVEAPI"],
    }),

    getLongDriveStats: build.query({
      query: () => ({
        url: `/longdrive/admin/stats`,
        method: "GET",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      providesTags: ["LONGDRIVEAPI"],
    }),
  }),
});

export const {
  // User hooks
  useGetAllLongDriveBookingsQuery,
  useGetUserLongDriveBookingsQuery,
  useGetLongDriveBookingByIdQuery,
  useCreateLongDriveBookingMutation,
  useCancelLongDriveBookingMutation,
  useEstimateLongDriveFareMutation,

  // Driver hooks
  useGetDriverLongDriveBookingsQuery,
  useDriverAcceptBookingMutation,
  useDriverStartJourneyMutation,
  useDriverCompleteJourneyMutation,

  // Admin hooks
  useSetPricingMutation,
  useAssignDriverMutation,
  useUpdateBookingStatusMutation,
  useGetPendingReviewsQuery,
  useGetAvailableDriversQuery,
  useGetLongDriveStatsQuery,
} = LongDriveApi;
